import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import { applyChangeIfDateValid, validIfSameDayOrAfter } from '../../../../../../utils/datepicker';
import {
  CreateSaleEventMutation,
  CreateSaleEventMutationVariables,
  DeleteSaleEventMutation,
  DeleteSaleEventMutationVariables,
  GetAppShopBundlesSalesQuery,
  GetAppShopBundlesSalesQueryVariables,
  GetLiveOpsSaleEventConfigQuery,
  GetLiveOpsSaleEventConfigQueryVariables,
  GetSpendTierAudienceQuery,
  GetSubscriptionSaleOffersQuery,
  GetSubscriptionSaleOffersQueryVariables,
  PurchaseLimitEnum,
  SaleBundleConfig,
  SaleConfig,
  SaleEventConfig,
  SaleEventStatus,
  SaleTagType,
  SubscriptionSaleConfig,
  UpdateSaleBundleInput,
  UpdateSaleEventInput,
  UpdateSaleEventMutation,
  UpdateSaleEventMutationVariables,
  UpdateSaleInput,
  UpdateSubscriptionSaleInput,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { SaleRow } from './Sale';
import { AppSalesConfig } from '../typedef';
import {
  APP_SHOP_BUNDLES_SALES,
  CREATE_SALE_EVENT,
  DELETE_SALE_EVENT,
  LIVEOPS_SALE_EVENT_CONFIG_QUERY,
  LIVEOPS_SUB_SALES_OFFERS,
  SPEND_TIER_AUDIENCE_QUERY,
  UPDATE_SALE_EVENT,
  UPLOAD_SALE_EVENT_ASSET,
} from '../graphql';
import { getSaleDiscount, stringToEnum } from '../utils';
import AssetDisplay from '../../Items/components/AssetDisplay';
import DragUpload from '../../Items/components/DragUpload';
import { BundleRow } from './Bundle';
import { DailyDeal } from './DailyDeals';
import { Item } from '../../Items/components/typedef';
import { SubscriptionSaleRow } from './SubscriptionSale';
import AddSaleEventModal from './AddSaleEventModal';

// UI sentinel for "no subscription filter". Kept out of the state as undefined so the
// select never becomes uncontrolled; converted back to undefined on submit.
// Same pattern as the ChainedOffer screen, whose matcher this one now shares.
const ALL_TIERS = 'All';
type SubscriptionTierFilter = UserSubscriptionTier | typeof ALL_TIERS;

/**
 * Lifetime spend tiers, ordered high spend -> low, then non-payers. Labels and USD ranges match
 * the vanity-item spend breakdown (devtools/screens/VanityItemTools/OwnershipStatsCard.tsx) so the
 * two screens name the same segments the same way. Values are the GraphQL UserSpendTier enum.
 */
const SPEND_TIER_OPTIONS = [
  { tier: UserSpendTier.KRAKEN, label: 'Kraken', range: 'USD > 1,000' },
  { tier: UserSpendTier.WHALE, label: 'Whale', range: 'USD 100 - 1,000' },
  { tier: UserSpendTier.DOLPHIN, label: 'Dolphin', range: 'USD 20 - 100' },
  { tier: UserSpendTier.MINNOW, label: 'Minnow', range: 'USD 0 - 20' },
  { tier: UserSpendTier.NPAU, label: 'NPAU', range: 'never paid' },
] as const;

/**
 * Variables for the audience estimate. Local only until the next `yarn codegen` sees
 * SpendTierAudienceFiltersInput on the deployed schema - then delete this and use the generated
 * GetSpendTierAudienceQueryVariables instead.
 */
type SpendTierAudienceVariables = {
  filters: {
    userIds?: string[];
    excludedUserIds?: string[];
    userLevel?: number;
    userMinCreationDate?: Date;
    userMaxCreationDate?: Date;
    noSubscriptionBefore?: boolean;
  };
};

type Props = {
  saleEventId: string;
  appSalesConfig: AppSalesConfig;
  // updateSaleState: (saleEventId: string, saleId: string, updatedSale: SaleConfig) => void;
  onUpsertSaleEvent: (value: SaleEventConfig) => void;
  onRemoveSaleEvent: (_saleEventId: string) => void;
  /** Adds the freshly cloned event to the list and opens it. */
  onCloneCreated: (saleEvent: SaleEventConfig) => void;
};

export enum SaleEventAssetType {
  BACKGROUND = 'BACKGROUND',
  CHARACTER = 'CHARACTER',
}

export enum BUNDLE_TAGS {
  NEW = 'saleEvent.saleTags.NEW',
  SALE = 'saleEvent.saleTags.SALE',
  DEAL = 'saleEvent.saleTags.DEAL',
  BONUS = 'saleEvent.saleTags.BONUS',
  SPECIAL = 'saleEvent.saleTags.SPECIAL',
  BIG_DEAL = 'saleEvent.saleTags.BIG DEAL',
  BEST_VALUE = 'saleEvent.saleTags.BEST VALUE',
}

// Bundle display names are no longer mapped here: the bundle keeps the i18n key the backend
// lists in bundleNameOptions (see Bundle.tsx), so adding a name is a locale-file change only.

const getEnumKeyByValue = (objEnum: Record<string, string>, val: string) => {
  return Object.keys(objEnum)[Object.values(objEnum).indexOf(val)];
};

export function SaleEventData({
  saleEventId,
  appSalesConfig,
  onUpsertSaleEvent,
  onRemoveSaleEvent,
  onCloneCreated,
}: Props) {
  /**
   * State
   */
  // Currently displayed specialEvent
  /**
   * @type {[SaleEventConfig, Function]} Currently displayed SaleEvent
   */
  const [name, setName] = useState('');
  const [status, setStatus] = useState(SaleEventStatus.DRAFT);
  const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<SubscriptionTierFilter>(ALL_TIERS);
  const [filterUserIds, setFilterUserIds] = useState<string>('');
  const [filterExcludedUserIds, setFilterExcludedUserIds] = useState<string>('');
  // Empty = no spend filter at all, matching the backend semantics.
  const [filterSpendTiers, setFilterSpendTiers] = useState<UserSpendTier[]>([]);
  const [filterUserMinCreationDate, setFilterUserMinCreationDate] = useState<Date | undefined>();
  const [filterUserMaxCreationDate, setFilterUserMaxCreationDate] = useState<Date | undefined>();
  const [filterAppVersion, setFilterAppVersion] = useState<string | undefined>();
  const [filterUserLevel, setFilterUserLevel] = useState<number | undefined>();
  const [filterHadSub, setFilterHadSub] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [stopDate, setStopDate] = useState<Date | null>(null);
  const [priority, setPriority] = useState<number>(1);
  const [readonly, setReadonly] = useState(true);
  const [sales, setSales] = useState<Array<SaleConfig & { itemId?: string }>>([]);
  const [bundles, setBundles] = useState<SaleBundleConfig[]>([]);
  const [deals, setDeals] = useState<Partial<Item>[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionSaleConfig[]>([]);
  // pop-up state management ----------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showBackgroundImageDeletionWarning, setShowBackgroundImageDeletionWarning] = useState(false);
  const [showCharacterImageDeletionWarning, setShowCharacterImageDeletionWarning] = useState(false);

  const [assetsConfig, setAssetsConfig] = useState<
    NonNullable<GetLiveOpsSaleEventConfigQuery['liveOps']['saleEventConfig']>['assetsConfig']
  >({
    __typename: 'SaleEventAssetsConfig',
  });

  /**
   * FETCHING
   */
  const {
    data,
    loading: loadingSaleEvent,
    refetch,
  } = useQuery<GetLiveOpsSaleEventConfigQuery, GetLiveOpsSaleEventConfigQueryVariables>(
    LIVEOPS_SALE_EVENT_CONFIG_QUERY,
    {
      fetchPolicy: 'no-cache',
      variables: { saleEventId },
    }
  );

  const bundleSales =
    useQuery<GetAppShopBundlesSalesQuery, GetAppShopBundlesSalesQueryVariables>(APP_SHOP_BUNDLES_SALES).data?.liveOps
      .bundleSales ?? [];

  const {data: subscriptionSalesData} =
    useQuery<GetSubscriptionSaleOffersQuery, GetSubscriptionSaleOffersQueryVariables>(LIVEOPS_SUB_SALES_OFFERS);
  const subscriptionSales = subscriptionSalesData?.liveOps.initialSubscriptionOffers ?? [];
  const availableSubSaleOffers = subscriptionSales.map((val) => ({id: val.id, discount: val.discount }));

  // Audience sizes are an explicit action rather than a page-load query: LiveOps only needs them
  // while actually choosing tiers, and the count scans every payer row.
  const [loadSpendTierAudience, { data: spendTierAudienceData, loading: spendTierAudienceLoading }] = useLazyQuery<
    GetSpendTierAudienceQuery,
    SpendTierAudienceVariables
  >(SPEND_TIER_AUDIENCE_QUERY, { fetchPolicy: 'no-cache' });

  const spendTierAudienceByTier = useMemo(() => {
    const rows = spendTierAudienceData?.liveOps.spendTierAudience ?? [];
    return rows.reduce<Record<string, (typeof rows)[number]>>((acc, row) => ({ ...acc, [row.tier]: row }), {});
  }, [spendTierAudienceData]);

  const spendTierComputedAt = spendTierAudienceData?.liveOps.spendTierAudience?.find((row) => row.computedAt)
    ?.computedAt;

  /**
   * Matchers the estimate cannot evaluate over the whole population: the app version only exists
   * in the client's request header, and the subscription tier is a composite of four tables behind
   * a Redis cache. Both are still enforced at runtime, so the event reaches fewer users than the
   * count shows - which is worth saying out loud rather than letting LiveOps read it as final.
   * Every tier counts here, BASIC included: the matcher targets one tier exactly, so BASIC narrows
   * the audience to users without a subscription.
   */
  const audienceIgnoredMatchers = [
    filterAppVersion ? 'app version' : undefined,
    filterSubscriptionTier !== ALL_TIERS ? 'subscription tier' : undefined,
  ].filter((matcher): matcher is string => !!matcher);

  /**
   * Ticking every tier reads as "all users" but does not evaluate like it: against an explicit
   * list, a payer whose tier has not synced yet fails closed and misses the event. It is saved as
   * "All users" instead (the backend normalises it too, for API clients), so the checkboxes keep
   * doing exactly what they look like and the note below says what will be stored.
   */
  const allSpendTiersSelected = filterSpendTiers.length === SPEND_TIER_OPTIONS.length;

  const [updateSaleEvent, { loading: updateLoading }] = useMutation<
    UpdateSaleEventMutation,
    UpdateSaleEventMutationVariables
  >(UPDATE_SALE_EVENT, {
    onCompleted: ({ updateSaleEvent: _updateSaleEvent }) => {
      const updatedSaleEvent = _updateSaleEvent.saleEvent;
      // @ts-ignore
      onUpsertSaleEvent(updatedSaleEvent);
      setShowBackgroundImageDeletionWarning(false);
      setShowCharacterImageDeletionWarning(false);
      refetch();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const [deleteSaleEvent, { loading: deleteLoading }] = useMutation<
    DeleteSaleEventMutation,
    DeleteSaleEventMutationVariables
  >(DELETE_SALE_EVENT, {
    onCompleted: ({ deleteSaleEvent: _deleteSaleEvent }) => {
      if (!_deleteSaleEvent.saleEventIdDeleted) {
        alert("ERROR: Couldn't remove the current specialEvent");
        return;
      }
      onRemoveSaleEvent(saleEventId);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  /**
   * Clone gets its own mutation instances on purpose: the Save and Delete hooks above carry
   * onCompleted callbacks aimed at the event currently open, which would fire against the wrong
   * event here. The cloned event is read from the return values instead.
   */
  const [cloneCreateSaleEvent, { loading: cloneCreateLoading }] = useMutation<
    CreateSaleEventMutation,
    CreateSaleEventMutationVariables
  >(CREATE_SALE_EVENT);

  const [cloneUpdateSaleEvent, { loading: cloneUpdateLoading }] = useMutation<
    UpdateSaleEventMutation,
    UpdateSaleEventMutationVariables
  >(UPDATE_SALE_EVENT);

  const cloneLoading = cloneCreateLoading || cloneUpdateLoading;

  const [uploadBackgroundAsset, { loading: uploadBackgroundLoading }] = useMutation(UPLOAD_SALE_EVENT_ASSET, {
    onCompleted: ({ uploadSaleEventAsset: { saleEvent } }) => {
      setAssetsConfig({ ...assetsConfig, backgroundImage: saleEvent.assetsConfig.backgroundImage });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const [uploadCharacterAsset, { loading: uploadCharacterLoading }] = useMutation(UPLOAD_SALE_EVENT_ASSET, {
    onCompleted: ({ uploadSaleEventAsset: { saleEvent } }) => {
      setAssetsConfig({ ...assetsConfig, characterImage: saleEvent.assetsConfig.characterImage });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const loading = loadingSaleEvent || updateLoading || deleteLoading;

  const resetMinutes = (date: any) => {
    const newDate = new Date(date);
    newDate.setMinutes(0);
    return newDate;
  };

  function userIdsStringToArray(songIdsString: string): string[] | undefined {
    if (typeof songIdsString === 'string' && songIdsString.trim().length > 0) {
      return songIdsString.split(',').map((id) => id.trim());
    }
    return undefined;
  }

  function userIdsArrayToString(userIdsArray: string[] | string): string {
    if (Array.isArray(userIdsArray)) {
      return userIdsArray.join(', ');
    }
    return userIdsArray;
  }

  const handleBackgroundImageUpload = useCallback(
    (file) => {
      uploadBackgroundAsset({
        variables: {
          input: {
            file,
            saleEventId,
            type: SaleEventAssetType.BACKGROUND,
          },
        },
      });
    },
    [saleEventId, uploadBackgroundAsset]
  );

  const handleCharacterImageUpload = useCallback(
    (file) => {
      uploadCharacterAsset({
        variables: {
          input: {
            file,
            saleEventId,
            type: SaleEventAssetType.CHARACTER,
          },
        },
      });
    },
    [saleEventId, uploadCharacterAsset]
  );

  /**
   * STATE AUTO UPDATE FUNCTIONS
   */
  // set current specialEvent to the one fetched
  useEffect(() => {
    if (data && data.liveOps.saleEventConfig) {
      const fetchedSaleEvent = data.liveOps.saleEventConfig;
      setName(fetchedSaleEvent.name);
      setStatus(fetchedSaleEvent.status);
      setFilterSubscriptionTier(fetchedSaleEvent.filterSubscriptionTier ?? ALL_TIERS);
      setFilterUserMinCreationDate(
        fetchedSaleEvent.filterUserMinCreationDate
          ? resetMinutes(fetchedSaleEvent.filterUserMinCreationDate)
          : undefined
      );
      setFilterUserMaxCreationDate(
        fetchedSaleEvent.filterUserMaxCreationDate
          ? resetMinutes(fetchedSaleEvent.filterUserMaxCreationDate)
          : undefined
      );
      setFilterAppVersion(fetchedSaleEvent.filterAppVersion ?? undefined);
      setFilterUserLevel(fetchedSaleEvent.filterUserLevel ?? undefined);
      setFilterHadSub(fetchedSaleEvent.filterHadSub ?? false);
      setFilterUserIds(userIdsArrayToString(fetchedSaleEvent.filterUserIds ?? []));
      setFilterExcludedUserIds(userIdsArrayToString(fetchedSaleEvent.filterExcludedUserIds ?? []));
      setFilterSpendTiers(fetchedSaleEvent.filterSpendTiers ?? []);
      setStartDate(resetMinutes(fetchedSaleEvent.startDate));
      setStopDate(resetMinutes(fetchedSaleEvent.stopDate));
      setPriority(fetchedSaleEvent.priority);
      setDeals(fetchedSaleEvent.dailyItems as Partial<Item>[]);
      setSales(
        fetchedSaleEvent.sales.map((sale) =>
          ['vinyl', 'frame', 'sticker'].includes((sale.saleProductId as string)?.split('.')[0])
            ? {
                ...sale,
                itemId: sale.targetedProductId ?? undefined,
                targetedProductId: sale.saleProductId?.split('.').slice(0, 2).join('.'),
              }
            : sale
        )
      );
      setBundles(
        fetchedSaleEvent.packs.map((pack) => ({
          ...pack,
          name: pack.name ?? undefined,
          tag: pack.tag ? getEnumKeyByValue(BUNDLE_TAGS, pack.tag) : undefined,
        }))
      );
      setSubscriptions(fetchedSaleEvent.subscriptions);
      setReadonly(fetchedSaleEvent.isHardCoded);
      setAssetsConfig(fetchedSaleEvent.assetsConfig);
    }
  }, [data]);

  /**
   * CHANGES HANDLERS FUNCTIONS
   */

  /**
   * Maps the form state to an UpdateSaleEventInput. Shared by Save and Clone, so a matcher or
   * field added here reaches both paths - `id`, `name` and `status` are parameters because a clone
   * writes the same config to a different event under a different name.
   *
   * `newRowIds` is what makes a clone a copy rather than a second reference: sale and pack ids are
   * per-event (they are minted with uuid() when a row is added), so handing the copy the source's
   * ids would leave two events whose rows are indistinguishable by id. The rows are keyed on
   * `id-index` in the tables below, so React would reuse the same SaleRow/BundleRow instances when
   * switching between the source and the copy, and each row keeps its own state copy - which is
   * how edits made on one event ended up showing on the other. Subscription sales are excluded on
   * purpose: their id is an offer id from the shared catalogue, not a per-event row id.
   */
  const buildSaleEventInput = (
    id: string,
    overrides?: { name?: string; status?: SaleEventStatus; newRowIds?: boolean },
    newBundles?: SaleBundleConfig[]
  ): UpdateSaleEventInput => {
    const rowId = (currentId: string) => (overrides?.newRowIds ? uuid() : currentId);
    const updateSales: UpdateSaleInput[] = sales.map((sale) => ({
      id: rowId(sale.id),
      name: sale.name,
      ruleMaximumNumberOfClaim:
        !sale.purchaseLimitType || sale.purchaseLimitType === PurchaseLimitEnum.UNLIMITED
          ? null
          : sale.ruleMaximumNumberOfClaim,
      purchaseLimitType: sale.purchaseLimitType,
      saleProductId: sale.itemId
        ? `${sale.targetedProductId}.${sale.offerPercentage}.${sale.oldPrice}`
        : sale.saleProductId,
      startDate: sale.startDate,
      stopDate: sale.stopDate,
      tag: sale.tag,
      targetedProductId: sale.itemId ?? sale.targetedProductId,
    })) as UpdateSaleInput[];
    const updateBundles: UpdateSaleBundleInput[] = (newBundles ?? bundles).map((bundle) => ({
      id: rowId(bundle.id),
      name: bundle.name || undefined,
      ruleMaximumNumberOfClaim: bundle.ruleMaximumNumberOfClaim,
      purchaseLimitType: bundle.purchaseLimitType,
      backgroundImage: bundle.backgroundImage,
      skuId: bundle.skuId,
      offerPercentage: bundle.offerPercentage,
      displayTimer: bundle.displayTimer,
      tag: !bundle.tag || bundle.tag === 'None' ? undefined : stringToEnum(bundle.tag, BUNDLE_TAGS),
      items: bundle.items.map((item) => {
        return { itemId: item.itemId, type: item.type, amount: item.amount };
      }),
    })) as UpdateSaleBundleInput[];

    const updateSubscriptions: UpdateSubscriptionSaleInput[] = 
      subscriptions.map((val) => ({id: val.id, backgroundTheme: val.backgroundTheme}));
    return {
      id,
      name: overrides?.name ?? name,
      startDate,
      stopDate,
      priority,
      status: overrides?.status ?? status,
      sales: updateSales,
      packs: updateBundles,
      subscriptions: updateSubscriptions,
      dailyItems: deals.map((deal) => deal.id) as string[],
      filterSubscriptionTier: filterSubscriptionTier === ALL_TIERS ? undefined : filterSubscriptionTier,
      filterUserMinCreationDate,
      filterUserMaxCreationDate,
      filterHadSub,
      filterUserIds: userIdsStringToArray(filterUserIds),
      filterExcludedUserIds: userIdsStringToArray(filterExcludedUserIds),
      filterSpendTiers: filterSpendTiers.length > 0 && !allSpendTiersSelected ? filterSpendTiers : undefined,
      filterAppVersion,
      filterUserLevel,
      assetsConfig: {
        backgroundImage: assetsConfig.backgroundImage,
        characterImage: assetsConfig.characterImage,
      },
    };
  };

  const handleSave = (newBundles?: SaleBundleConfig[]) => {
    updateSaleEvent({ variables: { input: buildSaleEventInput(saleEventId, undefined, newBundles) } });
  };

  const handleDeleteSaleEvent = () => {
    deleteSaleEvent({
      variables: {
        input: {
          id: saleEventId,
        },
      },
    });
  };

  /**
   * Clone creates an empty event, then writes the current form config into it - the only two
   * mutations the API offers for this. They are not atomic: if the second call fails, the empty
   * draft is left in place and reported instead of deleted, because a failure that was really a
   * timeout may have applied the config anyway.
   *
   * The new event is always a DRAFT, whatever the source status is, so cloning a running event
   * can never put a second live event in front of players by accident. Its sales and bundles are
   * created with fresh ids (see buildSaleEventInput) so the copy is independent of the source.
   */
  const handleClone = async ({ name: cloneName }: { name: string }) => {
    setShowCloneModal(false);
    let createdId: string | undefined;
    try {
      const created = await cloneCreateSaleEvent({ variables: { input: { name: cloneName } } });
      const createdEvent = created.data?.createSaleEvent.saleEvent;
      if (!createdEvent) {
        throw new Error('createSaleEvent returned no sale event');
      }
      createdId = createdEvent.id;
      const updated = await cloneUpdateSaleEvent({
        variables: {
          input: buildSaleEventInput(createdEvent.id, {
            name: cloneName,
            status: SaleEventStatus.DRAFT,
            newRowIds: true,
          }),
        },
      });
      const clonedEvent = updated.data?.updateSaleEvent.saleEvent;
      if (!clonedEvent) {
        throw new Error('updateSaleEvent returned no sale event');
      }
      // The fragment asks for only `id` on dailyItems, so the payload is a narrower shape than
      // SaleEventConfig. The event list reads name/status/startDate, all of which are present -
      // the same assumption the rest of this screen already makes about query results.
      onCloneCreated(clonedEvent as unknown as SaleEventConfig);
    } catch (error) {
      console.error(error);
      alert(
        createdId
          ? `ERROR: "${cloneName}" was created but its configuration was not copied. Reload the page to find the empty draft, then delete it or press Save on it.`
          : `ERROR: could not create "${cloneName}" - nothing was changed.`
      );
    }
  };

  const handleChangeName = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleChangePlayerIds = useCallback((event) => {
    setFilterUserIds(event.target.value);
  }, []);

  const handleChangeExcludedPlayerIds = useCallback((event) => {
    setFilterExcludedUserIds(event.target.value);
  }, []);

  const handleToggleSpendTier = useCallback((tier: UserSpendTier) => {
    setFilterSpendTiers((old) => (old.includes(tier) ? old.filter((t) => t !== tier) : [...old, tier]));
  }, []);

  const handleIncludeAllSpendTiers = useCallback(() => {
    setFilterSpendTiers([]);
  }, []);

  const handleChangeAppVersion = useCallback((event) => {
    setFilterAppVersion(event.target.value);
  }, []);

  const handleChangeUserLevel = useCallback((event) => {
    setFilterUserLevel(!Number.isNaN(+event.target.value) ? +event.target.value : undefined);
  }, []);

  const handleChangeHasPurchasedSub = useCallback(() => {
    setFilterHadSub((old) => !old);
  }, []);

  const handleChangePriority = useCallback((event) => {
    setPriority(+event.target.value);
  }, []);

  const handleChangeStatus = useCallback((event) => {
    setStatus(event.currentTarget.value as SaleEventStatus);
  }, []);

  const handleChangeSubscriberStatus = useCallback((event) => {
    setFilterSubscriptionTier(event.currentTarget.value as SubscriptionTierFilter);
  }, []);

  const handleAddSale = () => {
    const initialTargetedProduct = appSalesConfig.targetedProducts[0];
    const newSale: SaleConfig = {
      __typename: 'SaleConfig',
      id: uuid(),
      oldPrice: -1,
      newPrice:
        'cost' in initialTargetedProduct.saleConfigs[0] &&
        typeof initialTargetedProduct.saleConfigs[0].cost !== 'number'
          ? initialTargetedProduct.saleConfigs[0].cost?.amount
          : -1,
      offerPercentage: getSaleDiscount(initialTargetedProduct.id),
      saleProductId: initialTargetedProduct.saleConfigs[0].id,
      targetedProductId: initialTargetedProduct.id,
      purchaseLimitType: PurchaseLimitEnum.UNLIMITED,
      ruleMaximumNumberOfClaim: undefined,
      tag: SaleTagType.SALE,
    };
    setSales((oldSales) => [...oldSales, newSale]);
  };

  const handleAddDeal = () => {
    const newDeal: Partial<Item> = {
      id: '',
    };
    setDeals((oldDeals) => [...oldDeals, newDeal]);
  };

  const handleAddBundle = () => {
    const newBundle: SaleBundleConfig = {
      __typename: 'SaleBundleConfig',
      id: uuid(),
      skuId: bundleSales[0].id,
      offerPercentage: null,
      backgroundImage: null,
      displayTimer: true,
      items: [],
      purchaseLimitType: PurchaseLimitEnum.PER_SALE,
      ruleMaximumNumberOfClaim: 1,
    };
    setBundles((oldBundles) => {
      const newBundles = [...oldBundles, newBundle];
      handleSave(newBundles);
      return newBundles;
    });
  };

  const handleSaleUpdate = (updatedSale: SaleConfig) => {
    setSales((oldSales) => {
      const newSales = oldSales.map((sale) => {
        if (sale.id === updatedSale.id) {
          return updatedSale;
        }
        return sale;
      });
      return newSales;
    });
    return updatedSale;
  };

  const handleDealUpdate = (updatedDeal: Partial<Item>, index: number) => {
    setDeals((oldDeals) => {
      const newDeals = [...oldDeals];
      newDeals.splice(index, 1, updatedDeal);
      return newDeals;
    });
    return updatedDeal;
  };

  const handleAddSubSale = () => {
    const newSubSale: SubscriptionSaleConfig = {
      __typename: 'SubscriptionSaleConfig',
      discount: 0,
      id: 'empty',
    };
    setSubscriptions((oldVal) => [...oldVal, newSubSale]);
  };

  const handleSubUpdate = (updated: SubscriptionSaleConfig, index: number) => {
    setSubscriptions((oldSubs) => {
      const newSubs = [...oldSubs];
      newSubs[index] = updated;
      return newSubs;
    });
    return updated;
  };

  const handleSubDelete = (subId: number) => {
    setSubscriptions((oldSubs) => {
      return oldSubs.filter((val, idx) => idx !== subId);
    });
  };

  const handleBundleUpdate = (updatedBundle: SaleBundleConfig) => {
    setBundles((oldBundles) => {
      const newBundles = oldBundles.map((bundle) => {
        if (bundle.id === updatedBundle.id) {
          return updatedBundle;
        }
        return bundle;
      });
      return newBundles;
    });
    return updatedBundle;
  };

  const handleSaleDelete = (deleteSale: SaleConfig) => {
    setSales((oldSales) => oldSales.filter((sale) => sale.id !== deleteSale.id));
    return deleteSale;
  };

  const handleDealDelete = (index: number) => {
    setDeals((oldDeals) => {
      const newDeals = [...oldDeals];
      newDeals.splice(index, 1);
      return newDeals;
    });
  };

  const handleBundleDelete = (deleteBundle: SaleBundleConfig) => {
    setBundles((oldBundles) => oldBundles.filter((bundle) => bundle.id !== deleteBundle.id));
    return deleteBundle;
  };

  return loading ? (
    <LoadingLogo show />
  ) : (
    <>
      <Container className="pb-4">
        <Row className="mb-3">
          <Col md={3}>Id:</Col>
          <Col md={5}>{saleEventId}</Col>
          <Col className="d-flex justify-content-end">
            {/*
              Not disabled by `readonly`: cloning never touches the source event, and turning a
              hardcoded (read-only) event into an editable copy is one of the main uses.
            */}
            <Button
              className="mr-2"
              onClick={() => setShowCloneModal(true)}
              variant="warning"
              disabled={cloneLoading}
              title="Create a new draft event with this configuration"
            >
              <i className="fas fa-copy mr-2" />
              {cloneLoading ? 'Cloning…' : 'Clone'}
            </Button>
            <Button
              className="mr-2"
              onClick={() => {
                setShowDeleteModal(true);
              }}
              variant="danger"
              disabled={readonly}
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            {readonly || (
              <Button onClick={() => handleSave()} variant="success" type="submit">
                Save
              </Button>
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Name:</Col>
          <Col md={5}>
            <Form.Control disabled={readonly} value={name} onChange={handleChangeName} type="text" />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Start date:</Col>
          <Col md={5}>
            {readonly ? (
              startDate?.toLocaleString()
            ) : (
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStartDate)}
                isValidDate={validIfSameDayOrAfter}
                // @ts-ignore
                value={startDate ?? undefined}
              />
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Stop date:</Col>
          <Col md={5}>
            {readonly ? (
              stopDate?.toLocaleString()
            ) : (
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStopDate)}
                isValidDate={validIfSameDayOrAfter}
                // @ts-ignore
                value={stopDate ?? undefined}
              />
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Priority:</Col>
          <Col md={2}>
            <Form.Group controlId="sale-event-status">
              <Form.Control
                as="select"
                disabled={readonly}
                type="text"
                value={priority}
                onChange={handleChangePriority}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Status:</Col>
          <Col md={2}>
            <Form.Group controlId="sale-event-status">
              <Form.Control as="select" disabled={readonly} type="text" value={status} onChange={handleChangeStatus}>
                {Object.keys(SaleEventStatus).map((option) => (
                  <option key={option} value={stringToEnum(option, SaleEventStatus)}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <hr />
        <h4>Matchers:</h4>

        <Row className="mb-3">
          <Col md={3}>Minimum Registration Date:</Col>
          <Col md={5}>
            {readonly ? (
              filterUserMinCreationDate?.toLocaleString()
            ) : (
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterUserMinCreationDate)}
                // @ts-ignore
                value={filterUserMinCreationDate ?? undefined}
              />
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Maximum Registration Date:</Col>
          <Col md={5}>
            {readonly ? (
              filterUserMaxCreationDate?.toLocaleString()
            ) : (
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterUserMaxCreationDate)}
                // @ts-ignore
                value={filterUserMaxCreationDate ?? undefined}
              />
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Min App Version:</Col>
          <Col md={5}>
            <Form.Control type="text" disabled={readonly} value={filterAppVersion} onChange={handleChangeAppVersion} />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>Min User Level:</Col>
          <Col md={5}>
            <Form.Control type="text" disabled={readonly} value={filterUserLevel} onChange={handleChangeUserLevel} />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>User Subscription Status:</Col>
          <Col md={2}>
            <Form.Group controlId="sale-event-subscriber-status">
              <Form.Control
                as="select"
                disabled={readonly}
                type="text"
                value={filterSubscriptionTier}
                onChange={handleChangeSubscriberStatus}
              >
                <option value={ALL_TIERS}>All</option>
                {Object.keys(UserSubscriptionTier).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Has purchased subscription before:</Col>
          <Col md={2}>
                <Form.Check type="checkbox" checked={filterHadSub} onChange={handleChangeHasPurchasedSub}/>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            User Ids: <br />
            (separated by commas &quot;,&quot;)
          </Col>
          <Col md={8}>
            <Form.Control
              as="textarea"
              disabled={readonly}
              value={filterUserIds.toString()}
              onChange={handleChangePlayerIds}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            Spend Tier (PAU status):
            <br />
            <small className="text-muted">Lifetime spend, refreshed daily</small>
          </Col>
          <Col md={8}>
            <Form.Check
              type="radio"
              id="sale-event-spend-tier-all"
              disabled={readonly}
              checked={filterSpendTiers.length === 0}
              onChange={handleIncludeAllSpendTiers}
              label={
                <>
                  All users <small className="text-muted">no spend filter</small>
                </>
              }
            />
            {SPEND_TIER_OPTIONS.map(({ tier, label, range }) => (
              <Form.Check
                key={tier}
                type="checkbox"
                id={`sale-event-spend-tier-${tier}`}
                disabled={readonly}
                checked={filterSpendTiers.includes(tier)}
                onChange={() => handleToggleSpendTier(tier)}
                label={
                  <>
                    {label} <small className="text-muted">{range}</small>
                    {spendTierAudienceByTier[tier] && (
                      <small className="ml-2">
                        {spendTierAudienceByTier[tier].totalUsers.toLocaleString()} users
                      </small>
                    )}
                  </>
                }
              />
            ))}
            {allSpendTiersSelected && (
              <small className="text-muted">
                Every tier selected - this saves as <strong>All users</strong> (no spend filter), which also reaches
                payers whose tier has not been synced yet.
              </small>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              className="mt-2"
              disabled={spendTierAudienceLoading}
              onClick={() =>
                loadSpendTierAudience({
                  variables: {
                    filters: {
                      userIds: userIdsStringToArray(filterUserIds),
                      excludedUserIds: userIdsStringToArray(filterExcludedUserIds),
                      userLevel: filterUserLevel,
                      userMinCreationDate: filterUserMinCreationDate,
                      userMaxCreationDate: filterUserMaxCreationDate,
                      noSubscriptionBefore: filterHadSub,
                    },
                  },
                })
              }
            >
              {spendTierAudienceLoading ? 'Estimating…' : 'Estimate audience'}
            </Button>
            {spendTierComputedAt && (
              <div>
                <small className="text-muted">
                  Tiers computed {new Date(spendTierComputedAt).toLocaleString()} - lifetime spend, refreshed daily
                  (can be up to 24h behind). Counts every payer that matches this event&apos;s other matchers too,
                  whenever they last played. NPAU is every user without a tier row, so it is not counted here.
                  {audienceIgnoredMatchers.length > 0 && (
                    <>
                      {' '}
                      <strong>
                        Not accounted for: {audienceIgnoredMatchers.join(' and ')} - the real audience is smaller.
                      </strong>
                    </>
                  )}
                </small>
              </div>
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            Excluded User Ids: <br />
            (separated by commas &quot;,&quot;)
            <br />
            <small className="text-muted">Always wins over the matchers above</small>
          </Col>
          <Col md={8}>
            <Form.Control
              as="textarea"
              disabled={readonly}
              value={filterExcludedUserIds.toString()}
              onChange={handleChangeExcludedPlayerIds}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col>
            <Form.Label> Background Image </Form.Label>
            {assetsConfig && assetsConfig.backgroundImage && <AssetDisplay assetUrl={assetsConfig.backgroundImage} />}
            <DragUpload onUpload={handleBackgroundImageUpload} />
            <LoadingLogo show={uploadBackgroundLoading} />
            <Button
              disabled={readonly}
              className="mt-2"
              onClick={() => {
                setAssetsConfig({ ...assetsConfig, backgroundImage: '' });
                setShowBackgroundImageDeletionWarning(true);
              }}
              variant="danger"
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            {showBackgroundImageDeletionWarning && (
              <Alert variant="danger">Click on Save to confirm the deletion of an image</Alert>
            )}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col>
            <Form.Label> Character Image </Form.Label>
            {assetsConfig && assetsConfig.characterImage && <AssetDisplay assetUrl={assetsConfig.characterImage} />}
            <DragUpload onUpload={handleCharacterImageUpload} />
            <LoadingLogo show={uploadCharacterLoading} />
            <Button
              disabled={readonly}
              className="mt-2"
              onClick={() => {
                setAssetsConfig({ ...assetsConfig, characterImage: '' });
                setShowCharacterImageDeletionWarning(true);
              }}
              variant="danger"
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            {showCharacterImageDeletionWarning && (
              <Alert variant="danger">Click on Save to confirm the deletion of an image</Alert>
            )}
          </Col>
        </Row>
        <hr />
        <Card className="mt-3 mb-3">
          <Card.Header className="text-center">
            <Row className="justify-content-between">
              <Card.Title>High Priority Daily Items</Card.Title>
              <Button disabled={readonly} variant="success" className="align-self-center" onClick={handleAddDeal}>
                Add an Item
              </Button>
              <Button disabled={readonly} className="align-self-center ml-1" onClick={() => handleSave()}>
                Save Changes
              </Button>
            </Row>
          </Card.Header>
          <Card.Body>
            {loading || sales == null ? (
              <Spinner animation="border" />
            ) : (
              <>
                <Table>
                  <thead className="text-center">
                    <tr>
                      <th>Product Type</th>
                      <th>Item ID</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {deals.map((deal, dealIndex: number) => (
                      <DailyDeal
                        key={`${deal.id}-${dealIndex}`}
                        dealIndex={dealIndex}
                        fetchedDeal={deal}
                        handleDealUpdate={handleDealUpdate}
                        handleDealDelete={handleDealDelete}
                      />
                    ))}
                  </tbody>
                </Table>
                <hr />
              </>
            )}
          </Card.Body>
        </Card>
        <hr />
        <Card className="mt-3 mb-3">
          <Card.Header className="text-center">
              <Row className="justify-content-between">
                <Card.Title>Subscription Sales</Card.Title>
                <Button disabled={readonly} variant="success" className="align-self-center" onClick={handleAddSubSale}>
                  Add a Sale
                </Button>
                <Button disabled={readonly} className="align-self-center ml-1" onClick={() => handleSave()}>
                  Save Changes
                </Button>
              </Row>
            </Card.Header>
            <Card.Body>
            {loading || subscriptions == null ? (
              <Spinner animation="border" />
            ) : (
              <>
                <Table>
                  <thead className="text-center">
                    <tr>
                      <th>Offer Id</th>
                      <th>Discount</th>
                      <th>Background Theme</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {subscriptions.map((subSale, subSaleIndex: number) => (
                      <SubscriptionSaleRow
                        availableOffers={availableSubSaleOffers}
                        key={`${subSale.id}_${subSaleIndex}_${subSale.backgroundTheme}`}
                        myIndex={subSaleIndex}
                        subSale={subSale}
                        handleSubUpdate={handleSubUpdate}
                        handleSubDelete={handleSubDelete}
                      />
                    ))}
                  </tbody>
                </Table>
                <hr />
              </>
            )}
          </Card.Body>
        </Card>
        <hr />
        <Card className="mt-3 mb-3">
          <Card.Header className="text-center">
            <Row className="justify-content-between">
              <Card.Title>Sales</Card.Title>
              <Button disabled={readonly} variant="success" className="align-self-center" onClick={handleAddSale}>
                Add a Sale
              </Button>
              <Button disabled={readonly} className="align-self-center ml-1" onClick={() => handleSave()}>
                Save Changes
              </Button>
            </Row>
          </Card.Header>
          <Card.Body>
            {loading || sales == null ? (
              <Spinner animation="border" />
            ) : (
              <>
                <Table>
                  <thead className="text-center">
                    <tr>
                      <th>Target Product</th>
                      <th>Purchase Limits</th>
                      <th>Discount</th>
                      <th>Old Price</th>
                      <th>New Price</th>
                      {/* <th>Tag</th> Not implemented yet */}
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {sales.map((sale, saleIndex: number) => (
                      <SaleRow
                        key={`${sale.id}-${saleIndex}`}
                        fetchedSale={sale}
                        readonly={readonly}
                        appSalesConfig={appSalesConfig}
                        handleSaleUpdate={handleSaleUpdate}
                        handleSaleDelete={handleSaleDelete}
                      />
                    ))}
                  </tbody>
                </Table>
                <hr />
              </>
            )}
          </Card.Body>
        </Card>
        <hr />
        <Card className="mt-3 mb-3">
          <Card.Header className="text-center">
            <Row className="justify-content-between">
              <Card.Title>Sale Bundles</Card.Title>
              <Button disabled={readonly} variant="success" className="align-self-center" onClick={handleAddBundle}>
                Add a Sale Bundle
              </Button>
              <Button disabled={readonly} className="align-self-center ml-1" onClick={() => handleSave()}>
                Save Changes
              </Button>
            </Row>
          </Card.Header>
          <Card.Body>
            {loading || sales == null ? (
              <Spinner animation="border" />
            ) : (
              <>
                <Table>
                  <tbody className="text-center">
                    {bundles &&
                      bundles.map((bundle, bundleIndex: number) => (
                        <BundleRow
                          key={`${bundle.id}-${bundleIndex}`}
                          bundleSales={bundleSales}
                          fetchedBundle={bundle}
                          bundleIndex={bundleIndex}
                          saleEventId={saleEventId}
                          handleBundleUpdate={handleBundleUpdate}
                          handleBundleDelete={handleBundleDelete}
                        />
                      ))}
                  </tbody>
                </Table>
                <hr />
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>SaleEvent Removal</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>This cannot be undone. Are you sure to want to remove this Sale Event?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowDeleteModal(false);
              handleDeleteSaleEvent();
            }}
          >
            Remove Sale Event
          </Button>
        </Modal.Footer>
      </Modal>

      <AddSaleEventModal
        show={showCloneModal}
        loading={cloneLoading}
        onHide={() => setShowCloneModal(false)}
        onAdd={handleClone}
        defaultName={`${name} (copy)`}
        submitLabel="Clone"
        note="Copies what is on screen right now, including changes you have not saved. The new event is created as a draft."
      />
    </>
  );
}
